/**
 * PCM Theme Accessibility JavaScript
 * 
 * This file contains JavaScript functionality to enhance
 * accessibility features for the PCM theme.
 */

(function () {
  'use strict';

  // Main accessibility object
  window.pcmA11y = {

    /**
     * Initialize accessibility features
     */
    init: function () {
      this.setupAnnouncements();
      this.setupFocusManagement();
      this.setupKeyboardNavigation();
      this.setupFormEnhancements();
      this.setupModalAccessibility();
      this.setupAccordionAccessibility();
      this.enhanceFormAccessibility();
      this.handleDynamicContent();
    },

    /**
     * Announce messages to screen readers
     */
    // Announce message to screen readers
    announce: function (message, priority) {
      priority = priority || 'polite';
      const liveRegion = document.getElementById('pcm-aria-live-' + priority);
      if (liveRegion) {
        liveRegion.textContent = '';
        setTimeout(function () {
          liveRegion.textContent = message;
        }, 100);
      }
    },

    // Announce form status updates
    announceFormStatus: function (message, isError) {
      const formMessages = document.getElementById('pcm-form-messages');
      const statusRegion = document.getElementById('pcm-aria-status');

      if (isError && formMessages) {
        formMessages.textContent = '';
        setTimeout(function () {
          formMessages.textContent = message;
        }, 100);
      } else if (statusRegion) {
        statusRegion.textContent = '';
        setTimeout(function () {
          statusRegion.textContent = message;
        }, 100);
      }
    },

    // Announce loading states
    announceLoading: function (isLoading, message) {
      const loadingStatus = document.getElementById('pcm-loading-status');
      if (loadingStatus) {
        if (isLoading) {
          loadingStatus.textContent = message || 'Loading...';
        } else {
          loadingStatus.textContent = message || 'Loading complete';
          setTimeout(function () {
            loadingStatus.textContent = '';
          }, 1000);
        }
      }
    },

    // Enhanced form accessibility
    enhanceFormAccessibility: function () {
      const forms = document.querySelectorAll('form');

      forms.forEach(function (form) {
        // Add form submission handlers
        form.addEventListener('submit', function (e) {
          window.pcmA11y.announceLoading(true, 'Submitting form...');

          // Add loading state to submit button
          const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
          if (submitButton) {
            submitButton.setAttribute('aria-busy', 'true');
            const originalText = submitButton.textContent || submitButton.value;
            submitButton.dataset.originalText = originalText;

            if (submitButton.textContent) {
              submitButton.textContent = 'Submitting...';
            } else {
              submitButton.value = 'Submitting...';
            }

            submitButton.disabled = true;
          }
        });

        // Add input validation feedback
        const inputs = form.querySelectorAll('input, select, textarea');
        inputs.forEach(function (input) {
          input.addEventListener('invalid', function (e) {
            const fieldName = input.getAttribute('aria-label') ||
              input.getAttribute('placeholder') ||
              input.name || 'This field';

            window.pcmA11y.announceFormStatus(
              fieldName + ' is required or invalid. Please check your entry.',
              true
            );
          });

          // Clear error state on valid input
          input.addEventListener('input', function () {
            if (input.validity.valid) {
              input.removeAttribute('aria-invalid');
              input.classList.remove('error');
            } else {
              input.setAttribute('aria-invalid', 'true');
              input.classList.add('error');
            }
          });
        });
      });
    },

    // Handle dynamic content updates
    handleDynamicContent: function () {
      // Observer for dynamic content changes
      if (window.MutationObserver) {
        const observer = new MutationObserver(function (mutations) {
          mutations.forEach(function (mutation) {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              // Check if new content includes interactive elements
              Array.from(mutation.addedNodes).forEach(function (node) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  // Re-enhance any new forms
                  let newForms = node.querySelectorAll ? node.querySelectorAll('form') : [];
                  if (node.tagName === 'FORM') {
                    newForms = [node];
                  }

                  if (newForms.length > 0) {
                    window.pcmA11y.enhanceFormAccessibility();
                  }

                  // Check for new images without alt text
                  let newImages = node.querySelectorAll ? node.querySelectorAll('img:not([alt])') : [];
                  if (node.tagName === 'IMG' && !node.hasAttribute('alt')) {
                    newImages = [node];
                  }

                  if (newImages.length > 0 && window.console) {
                    console.warn('New images added without alt text:', newImages);
                  }
                }
              });
            }
          });
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    },

    /**
     * Announce form errors
     */
    announceFormError: function (message) {
      this.announce(`Error: ${message}`, 'assertive');
    },

    /**
     * Announce form success
     */
    announceFormSuccess: function (message) {
      this.announce(`Success: ${message}`, 'polite');
    },

    /**
     * Setup ARIA live region management
     */
    setupAnnouncements: function () {
      // Ensure live regions exist
      if (!document.getElementById('aria-live-polite')) {
        const politeRegion = document.createElement('div');
        politeRegion.id = 'aria-live-polite';
        politeRegion.setAttribute('aria-live', 'polite');
        politeRegion.setAttribute('aria-atomic', 'false');
        politeRegion.className = 'sr-only';
        document.body.appendChild(politeRegion);
      }

      if (!document.getElementById('aria-live-assertive')) {
        const assertiveRegion = document.createElement('div');
        assertiveRegion.id = 'aria-live-assertive';
        assertiveRegion.setAttribute('aria-live', 'assertive');
        assertiveRegion.setAttribute('aria-atomic', 'true');
        assertiveRegion.className = 'sr-only';
        document.body.appendChild(assertiveRegion);
      }
    },

    /**
     * Enhanced focus management
     */
    setupFocusManagement: function () {
      const self = this;

      // Skip link functionality
      document.querySelectorAll('.skip-link').forEach(function(skipLink) {
        skipLink.addEventListener('click', function (e) {
          const target = this.getAttribute('href');
          const targetElement = document.querySelector(target);

          if (targetElement) {
            e.preventDefault();

            // Ensure target can receive focus
            if (!targetElement.getAttribute('tabindex')) {
              targetElement.setAttribute('tabindex', '-1');
            }

            // Smooth scroll to target
            targetElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });

            // Focus the target after a short delay
            setTimeout(function () {
              targetElement.focus();

              // Announce the action
              const targetName = targetElement.getAttribute('aria-label') ||
                (targetElement.querySelector('h1, h2, h3') && targetElement.querySelector('h1, h2, h3').textContent) ||
                target.substring(1);
              self.announce(`Jumped to ${targetName}`);
            }, 100);
          }
        });
      });

      // Improve focus visibility on mobile
      if ('ontouchstart' in window) {
        document.addEventListener('touchstart', function(e) {
          const target = e.target;
          if (target.matches('a, button, input, select, textarea')) {
            target.classList.add('touch-focused');
          }
        });

        document.addEventListener('touchend', function(e) {
          const target = e.target;
          if (target.matches('a, button, input, select, textarea')) {
            target.classList.remove('touch-focused');
          }
        });
        
        document.addEventListener('blur', function(e) {
          const target = e.target;
          if (target.matches('a, button, input, select, textarea')) {
            target.classList.remove('touch-focused');
          }
        }, true);
      }

      // Focus trap for modals (if not handled by Bootstrap)
      let lastFocusedElement = null;
      
      document.addEventListener('show.bs.modal', function(e) {
        lastFocusedElement = document.activeElement;
      });

      document.addEventListener('shown.bs.modal', function(e) {
        const modal = e.target;
        const focusableElements = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

        if (focusableElements.length) {
          focusableElements[0].focus();
        }
      });

      document.addEventListener('hidden.bs.modal', function(e) {
        if (lastFocusedElement) {
          lastFocusedElement.focus();
          lastFocusedElement = null;
        }
      });
    },

    /**
     * Enhanced keyboard navigation
     */
    setupKeyboardNavigation: function () {
      // Escape key handling for dropdowns and modals
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
          // Close open dropdowns
          document.querySelectorAll('.dropdown-toggle[aria-expanded="true"]').forEach(function(dropdown) {
            const bsDropdown = bootstrap.Dropdown.getInstance(dropdown);
            if (bsDropdown) bsDropdown.hide();
          });

          // Close modals
          document.querySelectorAll('.modal.show').forEach(function(modal) {
            const bsModal = bootstrap.Modal.getInstance(modal);
            if (bsModal) bsModal.hide();
          });

          // Collapse open accordions (optional)
          document.querySelectorAll('.accordion-collapse.show').forEach(function(collapse) {
            const bsCollapse = bootstrap.Collapse.getInstance(collapse);
            if (bsCollapse) bsCollapse.hide();
          });
        }
      });

      // Arrow key navigation for dropdowns
      document.addEventListener('keydown', function (e) {
        const dropdown = e.target.closest('.dropdown-menu');
        if (dropdown) {
          const items = Array.from(dropdown.querySelectorAll('.dropdown-item:not([style*="display: none"])'));
          const currentIndex = items.indexOf(document.activeElement);

          if (e.key === 'ArrowDown' || e.keyCode === 40) {
            e.preventDefault();
            const nextIndex = (currentIndex + 1) % items.length;
            items[nextIndex].focus();
          } else if (e.key === 'ArrowUp' || e.keyCode === 38) {
            e.preventDefault();
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
            items[prevIndex].focus();
          }
        }

        // Home/End key navigation
        const navContainer = e.target.closest('.nav, .dropdown-menu');
        if (navContainer) {
          const items = Array.from(navContainer.querySelectorAll('a:not([style*="display: none"]), button:not([disabled]):not([style*="display: none"])'));

          if (e.key === 'Home' || e.keyCode === 36) {
            e.preventDefault();
            items[0].focus();
          } else if (e.key === 'End' || e.keyCode === 35) {
            e.preventDefault();
            items[items.length - 1].focus();
          }
        }
      });
    },

    /**
     * Form accessibility enhancements
     */
    setupFormEnhancements: function () {
      const self = this;

      // Live validation feedback
      document.addEventListener('blur', function(e) {
        const field = e.target;
        if (field.matches('input[required], select[required], textarea[required]')) {
          const errorContainer = field.parentElement.querySelector('.form-error');

          if (!field.validity.valid) {
            const errorMessage = field.validationMessage || 'This field is required';

            if (!errorContainer) {
              const errorId = field.id + '-error';
              const newError = document.createElement('div');
              newError.id = errorId;
              newError.className = 'form-error text-danger';
              newError.setAttribute('role', 'alert');
              newError.textContent = errorMessage;

              field.parentElement.appendChild(newError);

              // Update aria-describedby
              const describedBy = field.getAttribute('aria-describedby');
              const newDescribedBy = describedBy ? `${describedBy} ${errorId}` : errorId;
              field.setAttribute('aria-describedby', newDescribedBy);
            } else {
              errorContainer.textContent = errorMessage;
            }

            field.setAttribute('aria-invalid', 'true');
            self.announceFormError(errorMessage);
          } else {
            if (errorContainer) {
              errorContainer.remove();
            }
            field.removeAttribute('aria-invalid');

            // Clean up aria-describedby
            const describedBy = field.getAttribute('aria-describedby');
            if (describedBy) {
              const errorId = field.id + '-error';
              const newDescribedBy = describedBy.replace(errorId, '').replace(/\s+/g, ' ').trim();
              if (newDescribedBy) {
                field.setAttribute('aria-describedby', newDescribedBy);
              } else {
                field.removeAttribute('aria-describedby');
              }
            }
          }
        }
      }, true);

      // Form submission feedback
      document.addEventListener('submit', function(e) {
        const form = e.target;
        if (form.tagName === 'FORM') {
          const submitBtn = form.querySelector('[type="submit"]');

          if (submitBtn) {
            const originalText = submitBtn.textContent;
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<span class="loading-spinner" aria-hidden="true"></span> Submitting...';
            submitBtn.setAttribute('aria-describedby', 'loading-status');

            // Add loading status for screen readers
            if (!document.getElementById('loading-status')) {
              const loadingStatus = document.createElement('div');
              loadingStatus.id = 'loading-status';
              loadingStatus.className = 'sr-only';
              loadingStatus.setAttribute('aria-live', 'polite');
              loadingStatus.textContent = 'Form is being submitted, please wait.';
              document.body.appendChild(loadingStatus);
            }

            window.pcmA11y.announce('Form is being submitted, please wait.');
          }
        }
      });
    },

    /**
     * Modal accessibility enhancements
     */
    setupModalAccessibility: function () {
      // Ensure modals have proper ARIA attributes
      document.querySelectorAll('.modal').forEach(function(modal) {
        const title = modal.querySelector('.modal-title');

        if (title && !modal.getAttribute('aria-labelledby')) {
          const titleId = title.id || 'modal-title-' + Date.now();
          title.id = titleId;
          modal.setAttribute('aria-labelledby', titleId);
        }

        if (!modal.getAttribute('aria-describedby')) {
          const body = modal.querySelector('.modal-body');
          if (body) {
            const bodyId = 'modal-body-' + Date.now();
            body.id = bodyId;
            modal.setAttribute('aria-describedby', bodyId);
          }
        }
      });

      // Announce modal state changes
      document.addEventListener('shown.bs.modal', function(e) {
        const modal = e.target;
        const titleElement = modal.querySelector('.modal-title');
        const title = titleElement ? titleElement.textContent : 'Modal dialog';
        window.pcmA11y.announce(`${title} opened`);
      });

      document.addEventListener('hidden.bs.modal', function(e) {
        const modal = e.target;
        const titleElement = modal.querySelector('.modal-title');
        const title = titleElement ? titleElement.textContent : 'Modal dialog';
        window.pcmA11y.announce(`${title} closed`);
      });
    },

    /**
     * Accordion accessibility enhancements
     */
    setupAccordionAccessibility: function () {
      // Announce accordion state changes
      document.addEventListener('shown.bs.collapse', function(e) {
        const collapse = e.target;
        const button = document.querySelector(`[data-bs-target="#${collapse.id}"]`);
        const title = button ? button.textContent.trim() : 'Accordion section';
        window.pcmA11y.announce(`${title} expanded`);
      });

      document.addEventListener('hidden.bs.collapse', function(e) {
        const collapse = e.target;
        const button = document.querySelector(`[data-bs-target="#${collapse.id}"]`);
        const title = button ? button.textContent.trim() : 'Accordion section';
        window.pcmA11y.announce(`${title} collapsed`);
      });
    },

    /**
     * Utility function to check if element is visible
     */
    isVisible: function (element) {
      const computed = window.getComputedStyle(element);
      return element.offsetWidth > 0 && element.offsetHeight > 0 && 
             computed.visibility !== 'hidden' && computed.opacity !== '0';
    },

    /**
     * Utility function to get focusable elements
     */
    getFocusableElements: function (container) {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]'
      ].join(',');

      return Array.from(container.querySelectorAll(focusableSelectors)).filter(el => this.isVisible(el));
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      window.pcmA11y.init();

      // Announce that accessibility features are loaded
      setTimeout(function () {
        window.pcmA11y.announce('Accessibility features loaded');
      }, 500);
    });
  } else {
    window.pcmA11y.init();
    setTimeout(function () {
      window.pcmA11y.announce('Accessibility features loaded');
    }, 500);
  }

  // Handle dynamic content loading
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length > 0) {
        Array.from(mutation.addedNodes).forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const forms = node.querySelectorAll('form');
            const modals = node.querySelectorAll('.modal');
            const accordions = node.querySelectorAll('.accordion');
            
            if (forms.length) window.pcmA11y.setupFormEnhancements();
            if (modals.length) window.pcmA11y.setupModalAccessibility();
            if (accordions.length) window.pcmA11y.setupAccordionAccessibility();
          }
        });
      }
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Export for global access
  window.PCMAccessibility = window.pcmA11y;

})();
